import { type FunctionComponent } from "react";

import { tracking } from "shared/data";

import type { FallbackReason, WidgetVersion } from "scmp-app/pages/home/ai-widget-fetch";
import {
  AiCftWidgetWrapper,
  AiSpotlightWidgetWrapper,
  AiStoryPackageOneWidgetWrapper,
  AiStoryPackageTwoWidgetWrapper,
  CFTWidget,
  SpotlightWidget,
} from "scmp-app/pages/home/styles";
import type { batchQueryAbWidgetQuery } from "scmp-app/queries/__generated__/batchQueryAbWidgetQuery.graphql";
import type { chinaFutureTechStoryPackageHomeV2Query$key } from "scmp-app/queries/__generated__/chinaFutureTechStoryPackageHomeV2Query.graphql";
import type { spotlightHomeV2Query$key } from "scmp-app/queries/__generated__/spotlightHomeV2Query.graphql";

import { MidPageWidgetsQueueData } from "./queue-data";
import { EMPTY_MID_PAGE_QUEUE_DATA, type MidPageQueueData } from "./types";

type Props = {
  data: batchQueryAbWidgetQuery["response"] | null;
  fallbackData: unknown;
  fallbackReason?: FallbackReason;
  recommendationId?: string;
  slotModes: SlotModes;
  topicNames: {
    cftTopicName?: string;
    storyPackageOneTopicName?: string;
    storyPackageTwoTopicName?: string;
  };
  widgetVersion?: WidgetVersion;
};

type SlotModes = Record<
  "cft" | "spotlight" | "storyPackageOne" | "storyPackageTwo",
  "ai" | "queue"
>;

/**
 * SCMPWEB-9641: renders one shared visual component per slot whether the data
 * comes from the AI recommendation batch (config ON) or the CMS node queue
 * (config OFF / global API fallback). Queue-driven story-package slots reuse
 * the same AiPrimary/AiSecondary widgets via the fallback query's Relay
 * fragments.
 *
 * The queue-data read is isolated in `MidPageWidgetsQueueData` so this
 * component never calls `useFragment` with a missing reference (which happens
 * on the pure-AI path when no slot is config-forced) or a reference whose
 * fragment was `@include`-excluded from the parent query.
 */
export const MidPageWidgets: FunctionComponent<Props> = ({
  data,
  fallbackData,
  fallbackReason,
  recommendationId,
  slotModes,
  topicNames,
  widgetVersion,
}) => {
  const fallbackProvided = fallbackData !== null && fallbackData !== undefined;

  const render = (queueData: MidPageQueueData) => {
    const storyPackageOneIsAi = slotModes.storyPackageOne === "ai";
    const storyPackageTwoIsAi = slotModes.storyPackageTwo === "ai";
    const cftIsAi = slotModes.cft === "ai";
    const spotlightIsAi = slotModes.spotlight === "ai";

    const queuePackageTopEdges = queueData.packageTopEdges;
    const queuePackageTopTopicName = queueData.packageTopTopicName;
    const queuePackageTopTopicReference = queueData.packageTopTopicReference;
    const queueStoryPackageTwoEdges = queueData.storyPackageTwoEdges;
    const queueStoryPackageTwoTopicName = queueData.storyPackageTwoTopicName;
    const queueStoryPackageTwoTopicReference = queueData.storyPackageTwoTopicReference;

    return (
      <>
        <AiStoryPackageOneWidgetWrapper
          edges={storyPackageOneIsAi ? (data?.widget1?.edges ?? []) : queuePackageTopEdges}
          fallbackReason={!storyPackageOneIsAi && fallbackReason ? fallbackReason : undefined}
          recommendationId={storyPackageOneIsAi ? recommendationId : ""}
          topicName={
            storyPackageOneIsAi ? topicNames.storyPackageOneTopicName : queuePackageTopTopicName
          }
          topicReference={
            storyPackageOneIsAi
              ? (data?.storyPackageOneTopic ?? undefined)
              : queuePackageTopTopicReference
          }
          widgetName="feature_package"
          widgetVersion={widgetVersion}
          withBlockSection
        />
        <AiStoryPackageTwoWidgetWrapper
          edges={storyPackageTwoIsAi ? (data?.widget2?.edges ?? []) : queueStoryPackageTwoEdges}
          fallbackReason={!storyPackageTwoIsAi && fallbackReason ? fallbackReason : undefined}
          fallbackTitle=""
          moduleValue={tracking.module.StoryPackageTwo}
          recommendationId={storyPackageTwoIsAi ? recommendationId : ""}
          topicName={
            storyPackageTwoIsAi
              ? topicNames.storyPackageTwoTopicName
              : queueStoryPackageTwoTopicName
          }
          topicReference={
            storyPackageTwoIsAi
              ? (data?.storyPackageTwoTopic ?? undefined)
              : queueStoryPackageTwoTopicReference
          }
          widgetName="feature_package_2"
          widgetVersion={widgetVersion}
          withBlockSection
        />
        {cftIsAi ? (
          <AiCftWidgetWrapper
            edges={data?.widget3?.edges ?? []}
            fallbackTitle="Popular near you"
            moduleValue="popular_near_you"
            recommendationId={recommendationId}
            widgetName="popular_near_you"
            widgetVersion={widgetVersion}
          />
        ) : (
          <CFTWidget
            fallbackReason={fallbackReason}
            reference={fallbackData as chinaFutureTechStoryPackageHomeV2Query$key}
            widgetName="popular_near_you"
          />
        )}
        {spotlightIsAi ? (
          <AiSpotlightWidgetWrapper
            edges={data?.widget4?.edges ?? []}
            fallbackTitle="SCMP Spotlight"
            moduleValue={tracking.module.Spotlight}
            recommendationId={recommendationId}
            widgetName="scmp_spotlight"
            widgetVersion={widgetVersion}
          />
        ) : (
          <SpotlightWidget
            fallbackReason={fallbackReason}
            reference={fallbackData as spotlightHomeV2Query$key}
            widgetName="scmp_spotlight"
          />
        )}
      </>
    );
  };

  if (!fallbackProvided) {
    // Pure AI path — no queue fragments available; render with empty queue
    // data so queue-driven slots become a no-op.
    return render(EMPTY_MID_PAGE_QUEUE_DATA);
  }

  return <MidPageWidgetsQueueData fallbackData={fallbackData} onQueueData={render} />;
};

MidPageWidgets.displayName = "MidPageWidgets";
